import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { AgGrid } from '../AgGrid';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { getOrdersColumnDefinitions, GRID_NAMES } from '../../helpers';
import GridFilterBar from '../GridFilterBar/GridFilterBar';
import { getReviewedStatuses, updateReviewedStatus } from '../../services';
import queryString from 'query-string';

const styles = theme => ({});

export const ClientOrdersGrid = withRouter(
  withStyles(styles)(({ type, history, setPageTitle }) => {
    const [showGrid, setShowGrid] = useState(true);
    const [gridApi, setGridApi] = useState(null);
    const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfOrderLineId, setPdfOrderLineId] = useState(null);
    const [reviewedStatuses, setReviewedStatuses] = useState([]);
    const [selectedReviewedStatus, setSelectedReviewedStatus] = useState('');
    const [pdfOrderClosed, setPdfOrderClosed] = useState(false);

    const [query] = useState(queryString.parse(history.location.search));

    const gridType = type || query.gridType || 'all';

    const gridName = type
      ? type === 'open' ? 'CLIENT_GRID_OPEN' : 'CLIENT_GRID_CLOSED'
      : 'CLIENT_GRID';

    const gridNameId = type
      ? (type === 'open' ? GRID_NAMES.CLIENT_GRID_OPEN : GRID_NAMES.CLIENT_GRID_CLOSED)
      : GRID_NAMES.CLIENT_GRID;

    const columnDefinitions = getOrdersColumnDefinitions('client', gridType);

    const openOrdersFilterModel = {
      clientOrderStatus: { filterType: 'set', values: ['In Field', 'Assigned to Policyholder', 'In QA'] },
    };

    const closedOrdersFilterModel = {
      clientOrderStatus: { filterType: 'set', values: ['Closed'] },
    };

    useEffect(() => {
      if (query.status) {
        setPageTitle(`Orders - ${query.status}`);
      }
      getReviewedStatuses()
        .then(setReviewedStatuses)
        .catch(err => console.error('Could not load reviewed statuses:', err));
    }, []);

    const openPdfViewer = async (url, orderLineId, clientOrderStatus) => {
      if (!url) return;
      setPdfOrderLineId(orderLineId);
      setSelectedReviewedStatus('');
      setPdfOrderClosed(clientOrderStatus === 'Closed');
      setPdfLoading(true);
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPdfViewerUrl(blobUrl);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        window.open(url, '_blank');
      } finally {
        setPdfLoading(false);
      }
    };

    const closePdfViewer = () => {
      if (pdfViewerUrl) URL.revokeObjectURL(pdfViewerUrl);
      setPdfViewerUrl(null);
      setPdfOrderLineId(null);
      setSelectedReviewedStatus('');
      setPdfOrderClosed(false);
    };

    const handleSubmitReviewedStatus = async () => {
      if (!selectedReviewedStatus || !pdfOrderLineId) return;
      try {
        await updateReviewedStatus(pdfOrderLineId, selectedReviewedStatus);
        closePdfViewer();
        if (gridApi) gridApi.purgeInfiniteCache();
      } catch (err) {
        console.error('Failed to update reviewed status:', err);
      }
    };

    const handleGridReady = gridOptions => {
      if (gridOptions && gridOptions.api) {
        setGridApi(gridOptions.api);
      }
    };

    return (
      <div className={classnames('flex-1', 'card', 'border-left-green', 'col-center-center')}>
        <GridFilterBar
          gridNameId={gridNameId}
          gridApi={gridApi}
          myWorkFilterModel={openOrdersFilterModel}
          processedOrdersFilterModel={closedOrdersFilterModel}
          myWorkLabel="Open Orders"
          processedOrdersLabel="Closed Orders"
          defaultToAllOrders
        />
        {showGrid && (
          <AgGrid
            gridData={[]}
            columnDefs={columnDefinitions}
            handleCellClick={evt => evt.data && evt.data.location && openPdfViewer(evt.data.location, evt.data.orderTicket, evt.data.clientOrderStatus)}
            redirectOnClick={true}
            editOnClick={false}
            useDatasource={true}
            datasourceUrl={'/orders'}
            gridName={gridName}
            onGridReady={handleGridReady}
          />
        )}

        <Dialog
          open={pdfLoading || !!pdfViewerUrl}
          onClose={closePdfViewer}
          maxWidth={false}
          fullWidth
          PaperProps={{ style: { height: '90vh', maxWidth: '90vw' } }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
            <IconButton size="small" onClick={closePdfViewer}>
              <Close />
            </IconButton>
          </div>
          <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
            {pdfLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress color="primary" size={48} />
              </div>
            ) : pdfViewerUrl && (
              <iframe
                src={pdfViewerUrl}
                title="PDF Report"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }} disabled={!pdfOrderClosed}>
              <InputLabel>Reviewed Status</InputLabel>
              <Select
                value={selectedReviewedStatus}
                onChange={e => setSelectedReviewedStatus(e.target.value)}
                label="Reviewed Status"
              >
                {reviewedStatuses.map(s => (
                  <MenuItem key={s.reviewedStatusLookupId} value={s.reviewedStatusLookupId}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              style={{ backgroundColor: selectedReviewedStatus && pdfOrderClosed ? '#4caf50' : '#a0a0a0', color: '#fff' }}
              onClick={handleSubmitReviewedStatus}
              disabled={!selectedReviewedStatus || !pdfOrderClosed}
            >
              Process
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }),
);

ClientOrdersGrid.propTypes = {};
